/**
 * RichTextField — the single description-editor entry point (issue #333).
 *
 * CONTRACT: `value` in / `onChange` out are the raw **markdown** string — the
 * stored format. No conversion happens anywhere in this component tree (the
 * original HTML⇄md shim mis-assumed stored HTML and mangled markdown: turndown
 * escaped `**bold**` to literal asterisks — removed, don't reintroduce).
 *
 * Renders the markdown/WYSIWYG MDXEditor (lazy-loaded so its heavy chunk stays
 * out of the main bundle). The classic TinyMCE path is LEGACY: it has no user
 *-facing switch anymore (removed with the markdown-native cutover) and is kept
 * only for the `editor` prop (tests) or a programmatically-set pref. NOTE:
 * TinyMCE is an HTML editor — routing markdown through it writes HTML into a
 * markdown store; that is why it is hidden and why the parse-failure fallback
 * below is a plain textarea, NOT TinyMCE.
 *
 * RESILIENCE: MDX parses `<x` as JSX, so text like "speed < 100" can be
 * unparseable or even throw during render. Two nets — MDXEditor's onError and a
 * React error boundary — drop this mount to a plain source textarea bound to
 * the ORIGINAL markdown, so the user always gets a working editor and never
 * loses content. The fallback resets when the editor choice changes.
 */
import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import { usePref } from '../../services/prefs';

const MdxRichTextEditor = React.lazy(() => import('./MdxRichTextEditor'));

// The shared TinyMCE Cloud key (legacy path only).
const DEFAULT_TINYMCE_API_KEY = 'lssudq3rdc0bctyq83ruymuz755om0dbp4w5nnurqmh050ae';

// Minimal error boundary: any render/runtime crash inside the markdown editor
// flips this mount to the plain-source fallback via onCrash.
class MdxErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error) {
    if (this.props.onCrash) this.props.onCrash(error);
  }

  render() {
    // Render the fallback HERE, not via a parent setState from componentDidCatch:
    // React 19 discards state updates scheduled from a boundary's error lifecycle
    // while it commits the recovery render (worked on 18, silently lost on 19 —
    // the crash path rendered nothing). onCrash stays as a notification only.
    return this.state.crashed ? (this.props.fallback ?? null) : this.props.children;
  }
}

MdxErrorBoundary.propTypes = {
  onCrash: PropTypes.func,
  fallback: PropTypes.node,
  children: PropTypes.node,
};

export default function RichTextField({
  value,
  onChange,
  editor,
  tinymceInit,
  tinymceApiKey,
  minHeight = 210,
  placeholder = '',
}) {
  const pref = usePref('editor');
  const chosen = editor || pref || 'mdxeditor';
  // Set when MDXEditor rejects/crashes on the current content; cleared when the
  // editor choice changes.
  const [mdxFailed, setMdxFailed] = React.useState(false);
  React.useEffect(() => { setMdxFailed(false); }, [chosen]);

  if (!mdxFailed && chosen === 'tinymce') {
    // LEGACY (no UI reaches this): kept for tests/emergency rollback only.
    return (
      <TinyMCEEditor
        apiKey={tinymceApiKey || DEFAULT_TINYMCE_API_KEY}
        value={value}
        onEditorChange={onChange}
        init={tinymceInit}
      />
    );
  }

  // Plain-source fallback: raw markdown in, raw markdown out — dependency-free
  // and format-preserving (unlike TinyMCE, which would emit HTML). Rendered by
  // the mdxFailed branch (onParseError path) AND by the error boundary directly
  // on a render crash (see MdxErrorBoundary.render).
  const plainFallback = (
    <textarea
      data-testid="richtext-fallback"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', minHeight, boxSizing: 'border-box', padding: 8,
        border: '1px solid #ccc', borderRadius: 4,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13,
      }}
    />
  );

  if (mdxFailed) {
    return plainFallback;
  }

  return (
    <Suspense
      fallback={
        <div className="t-body muted3" style={{ padding: 8, minHeight }}>
          Loading editor…
        </div>
      }
    >
      <MdxErrorBoundary onCrash={() => setMdxFailed(true)} fallback={plainFallback}>
        <MdxRichTextEditor
          value={value}
          onChange={onChange}
          minHeight={minHeight}
          placeholder={placeholder}
          onParseError={() => setMdxFailed(true)}
        />
      </MdxErrorBoundary>
    </Suspense>
  );
}

RichTextField.propTypes = {
  // Markdown string (the stored format).
  value: PropTypes.string,
  // Called with the markdown string on change.
  onChange: PropTypes.func.isRequired,
  // Force a specific editor ('tinymce' is legacy/tests-only — no user UI sets it).
  editor: PropTypes.oneOf(['tinymce', 'mdxeditor']),
  // TinyMCE-only (legacy): the `init` config object and API key.
  tinymceInit: PropTypes.object,
  tinymceApiKey: PropTypes.string,
  minHeight: PropTypes.number,
  placeholder: PropTypes.string,
};
